export type RecordKind = 'card' | 'collection';
export type PresentationKind = 'note' | 'password' | 'card' | 'photoId' | 'general';

export type SemanticType =
  | 'text'
  | 'multiline'
  | 'url'
  | 'email'
  | 'phone'
  | 'username'
  | 'password'
  | 'pin'
  | 'card-number'
  | 'security-code'
  | 'id-number'
  | 'recovery-code'
  | 'totp-seed'
  | 'private-key'
  | 'seed-phrase'
  | 'secret'
  | 'secret-answer'
  | 'api-key'
  | 'access-token'
  | 'bank-account'
  | 'iban'
  | 'tax-id'
  | 'social-security-number'
  | 'date'
  | 'number';

export interface MemoryLine {
  lineId: string;
  label: string;
  value: string;
  semanticType: SemanticType;
  indexable: boolean;
  concealed: boolean;
  position: number;
}

export interface ReminderSpec {
  mode: 'cloud' | 'device';
  dueDate: string;
  localTime?: string;
  timezone?: string;
  allDay: boolean;
  recurrence?: 'none' | 'daily' | 'weekly' | 'monthly' | 'yearly';
  /** Set when the reminder is marked done. The due date is kept, so this only hides it
   *  from Upcoming and marks it complete. Absent means still outstanding. */
  completedAt?: string;
}

export interface AttachmentRef {
  attachmentId: string;
  slot?: 'front' | 'back' | 'note';
  name: string;
  mimeType: string;
  sizeBytes: number;
  thumbnailAttachmentId?: string;
  state: 'local' | 'pending' | 'uploaded' | 'failed';
}

export interface MemoryCard {
  schemaVersion: 1;
  recordId: string;
  presentationKind: PresentationKind;
  title: string;
  pinned: boolean;
  lines: MemoryLine[];
  collectionIds: string[];
  reminder?: ReminderSpec;
  attachmentRefs: AttachmentRef[];
  clientMetadata: {
    createdAt: string;
    updatedAt: string;
  };
}

export interface CollectionRecord {
  schemaVersion: 1;
  recordId: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

export type RecordPayload = MemoryCard | CollectionRecord;

export interface MaterializedRecord<T extends RecordPayload = RecordPayload> {
  recordId: string;
  revisionId: string;
  baseRevisionId: string | null;
  recordKind: RecordKind;
  payload: T;
  pending: boolean;
  conflicted: boolean;
  deleted: boolean;
}

export interface AppSettings {
  menuOrder: 'preferred' | 'alphabetical' | 'recent';
  categoryLabelMode: 'name' | 'icon' | 'both';
  rowSpacing: 'large' | 'medium' | 'compact';
  fontScale: 'small' | 'medium' | 'large';
  /** Chosen interface language. Absent means follow the browser's language. */
  language?: string;
  autoLockMinutes: number;
}

export const DEFAULT_SETTINGS: AppSettings = {
  menuOrder: 'preferred',
  categoryLabelMode: 'name',
  rowSpacing: 'large',
  fontScale: 'medium',
  autoLockMinutes: 5,
};

export function isMemoryCard(payload: RecordPayload): payload is MemoryCard {
  return 'presentationKind' in payload && Array.isArray(payload.lines);
}

export function isCollectionRecord(payload: RecordPayload): payload is CollectionRecord {
  return 'name' in payload && !('presentationKind' in payload);
}

export function nowIso(): string {
  return new Date().toISOString();
}

export function createLine(
  label: string,
  value: string,
  semanticType: SemanticType = 'text',
  options: Partial<Pick<MemoryLine, 'indexable' | 'concealed' | 'position' | 'lineId'>> = {},
): MemoryLine {
  return {
    lineId: options.lineId ?? crypto.randomUUID(),
    label,
    value,
    semanticType,
    indexable: options.indexable ?? true,
    concealed: options.concealed ?? false,
    position: options.position ?? 0,
  };
}

export function normalizeCard(card: MemoryCard): MemoryCard {
  return {
    ...card,
    collectionIds: [...new Set(card.collectionIds ?? [])],
    attachmentRefs: card.attachmentRefs ?? [],
    lines: [...card.lines]
      .map((line, index) => ({ ...line, position: Number.isFinite(line.position) ? line.position : index }))
      .sort((a, b) => a.position - b.position),
  };
}
